import {
    HttpClient,
    HttpContext,
    HttpEvent,
    HttpEventType,
    HttpParams,
    HttpResponse,
    HttpUploadProgressEvent,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, filter, map, Observable, of, switchMap } from 'rxjs';
import {
    UploadStatus,
    IJoditUploadResponse,
    IEmailBody,
    IEmail,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { LOGGER_REQUEST_NAME } from '@dxp/core/types';

@Injectable({
    providedIn: 'root',
})
export class ContentApiService {
    readonly #http = inject(HttpClient);

    getEmailBody(bodyId: string): Observable<IEmailBody> {
        return this.#http.get<IEmailBody>(
            `${environment.contentV2Url}/messages/email/${bodyId}`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Get Email Body)',
                ),
            },
        );
    }

    getEmailMessages(
        conversationId: string,
        threadId?: string,
    ): Observable<IEmail[]> {
        const byConversationUrl = `${environment.contentV2Url}/messages/email/byconversationid`;
        const byThreadUrl = threadId
            ? `${environment.contentV2Url}/messages/email/thread/${encodeURIComponent(threadId)}`
            : null;

        const fetchByConversation = (): Observable<IEmail[]> => {
            const params = new HttpParams().set(
                'conversationId',
                conversationId,
            );
            return this.#http.get<IEmail[]>(byConversationUrl, {
                params,
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Get Messages By Conversation)',
                ),
            });
        };

        // If no threadId, go straight to conversation lookup.
        if (!byThreadUrl) {
            return fetchByConversation();
        }

        // Otherwise try thread first, then fall back if empty.
        return this.#http
            .get<IEmail[]>(byThreadUrl, {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Get Messages In Thread)',
                ),
            })
            .pipe(
                switchMap(messages =>
                    messages && messages.length > 0
                        ? of(messages)
                        : fetchByConversation(),
                ),
            );
    }

    uploadEmailBody(body: IEmailBody): Observable<IEmailBody> {
        return this.#http.post<IEmailBody>(
            `${environment.contentV2Url}/messages/email`,
            body,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Upload Email Body)',
                ),
            },
        );
    }

    uploadFile(file: File): Observable<string> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return this.#http.post<string>(
            `${environment.contentUrl}/attachments?api-version=2`,
            formData,
            {
                headers: { accept: 'application/json' },
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Upload File)',
                ),
            },
        );
    }

    uploadJoditFile(file: File): Observable<UploadStatus> {
        const formData = new FormData();
        formData.append('file', file, file.name);

        const url = `${environment.contentUrl}/attachments/jodit`;

        let startTs: number | null = null;
        let prevTs: number | null = null;
        let prevLoaded = 0;
        const rates: number[] = [];
        const MAX_RATES = 5;

        return this.#http
            .request<IJoditUploadResponse>('POST', url, {
                body: formData,
                reportProgress: true,
                observe: 'events',
                headers: { accept: 'application/json' },
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Content Service (Jodit Upload)',
                ),
            })
            .pipe(
                filter(
                    (e: HttpEvent<IJoditUploadResponse>) =>
                        e.type === HttpEventType.UploadProgress ||
                        e.type === HttpEventType.Response,
                ),
                map((e): UploadStatus => {
                    if (e.type === HttpEventType.UploadProgress) {
                        const pe = e as HttpUploadProgressEvent;
                        const total = pe.total ?? 0;
                        const loaded = pe.loaded ?? 0;
                        const progress =
                            total > 0 ? Math.round((loaded * 100) / total) : 0;

                        const now = performance.now();
                        if (startTs == null && total > 0 && loaded > 0)
                            startTs = now;

                        if (prevTs != null && loaded > prevLoaded) {
                            const dt = (now - prevTs) / 1000;
                            if (dt > 0) {
                                const rate = (loaded - prevLoaded) / dt;
                                rates.push(rate);
                                if (rates.length > MAX_RATES) rates.shift();
                            }
                        }
                        prevTs = now;
                        prevLoaded = loaded;

                        const avgRate =
                            rates.length > 0
                                ? rates.reduce((a, b) => a + b, 0) /
                                  rates.length
                                : startTs
                                  ? loaded / ((now - startTs) / 1000)
                                  : 0;

                        let etaSeconds: number | null = null;
                        if (
                            total > 0 &&
                            loaded > 0 &&
                            loaded < total &&
                            avgRate > 0
                        ) {
                            etaSeconds = Math.ceil((total - loaded) / avgRate);
                        }

                        return {
                            type: 'progress',
                            progress,
                            etaSeconds,
                            loadedBytes: loaded,
                            totalBytes: total,
                        };
                    }

                    const resp = e as HttpResponse<IJoditUploadResponse>;
                    const body = resp.body;

                    if (!body || !body.success || !body.data?.files?.length) {
                        const msg =
                            body?.data?.messages?.[0] ?? 'Upload failed';
                        throw new Error(msg);
                    }

                    const { baseurl, files } = body.data;
                    const ref = files[0];
                    const absUrl = baseurl.endsWith('/')
                        ? `${baseurl}${ref}`
                        : `${baseurl}/${ref}`;

                    return { type: 'success', url: absUrl, response: body };
                }),

                catchError(err => {
                    return of<UploadStatus>({ type: 'error', error: err });
                }),
            );
    }

    deleteFile(directory: string, filename: string): Observable<void> {
        const url = `${environment.contentUrl}/attachments/${directory}/${filename}`;

        return this.#http.delete<void>(url, {
            context: new HttpContext().set(
                LOGGER_REQUEST_NAME,
                'Content Service (Delete File)',
            ),
        });
    }
}
